/**
 * GDPR ↔ Shared Questionnaire Designer data adapters.
 *
 * Used by the GDPR questionnaire edit page to convert between the existing
 * GDPR API contract and the shared Questionnaire Designer model.
 *
 * Model notes (from source inspection):
 *
 * GDPR admin detail API → flat questions array (snake_case flags):
 *   id, title, type, is_mandatory, is_deletable, options[],
 *   optional: section, sub_section, sort_index, front_id, based_on, icon,
 *   question_setting, system_data
 *
 * GDPR options (designer/backend):
 *   id, title, type?, reportingDeadlineHours?, deleted?, children?, layout?, …
 *   Category options often have reportingDeadlineHours without option.type.
 *   Radiobox options may store a follow-up field type on option.type (e.g. `date`)
 *   instead of nested `children`. The shared Designer expects that follow-up as a
 *   child sub-question (same idea as `mapQuestions` in helpers/questionnaire.js).
 *
 * GDPR save API (current Designer handleSubmit):
 *   { questionnaire: <id>, questions: [{ id?, title, type, sortIndex, isMandatory, isDeletable, options }] }
 *   Option save resolves reportingDeadlineHours === 'custom' via customReportingDeadlineHours.
 *   Legacy radiobox follow-ups are saved back as option.type (not children).
 *
 * Shared Designer defaultModel (TPDD consumer pattern):
 *   [ { elementType:'page', title, baseTitle, image?, elements: [
 *        { elementType:'question', id, dbID, questionType, title, baseTitle, isMandatory, isDeletable, basedOn?, options[] }
 *        | { elementType:'panel', title, baseTitle, elements: [ question… ] }
 *   ] } ]
 *
 * Shared Designer option:
 *   { id (front), dbID, title, baseTitle, type?, children?, …custom fields preserved }
 */

const identity = (value) => value;

const generateFrontId = () => `el-${Date.now()}-${Math.random()}`;

const isPlainObject = (value) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Question types whose options can carry a legacy follow-up field on `option.type`.
 * Matches the radiobox handling in helpers/questionnaire.js `mapQuestions`.
 */
const CHOICE_QUESTION_TYPES_WITH_LEGACY_FOLLOW_UP = ['radiobox'];

/**
 * Default Designer titles for follow-up children synthesized from option.type.
 */
export const FOLLOW_UP_CHILD_TITLE_BY_TYPE = {
    date: 'Select Date',
    text: 'Enter Text',
    textarea: 'Enter Text',
    file_upload: 'Upload File',
    switch: 'Select Option',
    select: 'Select Option',
    checkbox: 'Select Options',
    radiobox: 'Select Option',
    legal_entity: 'Select Legal Entity',
    country_list: 'Select Country',
    multi_select: 'Select Options',
    country_multi_select: 'Select Countries'
};

/**
 * @param {string|undefined} parentQuestionType
 * @returns {boolean}
 */
const supportsLegacyOptionTypeFollowUp = (parentQuestionType) => {
    return CHOICE_QUESTION_TYPES_WITH_LEGACY_FOLLOW_UP.includes(parentQuestionType);
};

/**
 * Build a Designer child title for a legacy follow-up field type.
 * Prefers backend `extended_title` when present (same as runtime mapQuestions).
 *
 * @param {string} fieldType
 * @param {Object} option
 * @param {Function} t
 * @returns {string}
 */
const resolveFollowUpChildTitle = (fieldType, option, t) => {
    if (option?.extended_title) {
        return t(option.extended_title);
    }

    const fallback =
        FOLLOW_UP_CHILD_TITLE_BY_TYPE[fieldType] ||
        `Enter ${String(fieldType || 'value')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())}`;

    return t(fallback);
};

/**
 * Synthesize a Designer sub-question child from a legacy GDPR option.type.
 *
 * @param {string} fieldType
 * @param {Object} option
 * @param {Function} t
 * @param {Array} [childOptions=[]] already-mapped Designer nested options
 * @returns {Object}
 */
const buildFollowUpChildFromOptionType = (
    fieldType,
    option,
    t,
    childOptions = []
) => {
    const title = resolveFollowUpChildTitle(fieldType, option, t);

    return {
        id: generateFrontId(),
        // No dbID — this child only exists in the Designer model.
        baseTitle: title,
        title,
        questionType: fieldType,
        isMandatory: false,
        isDeletable: true,
        options: childOptions
    };
};

/**
 * Remove keys whose values are undefined so payloads stay clean.
 * @param {Object} obj
 * @returns {Object}
 */
const omitUndefined = (obj) => {
    const result = {};
    Object.keys(obj || {}).forEach((key) => {
        if (obj[key] !== undefined) {
            result[key] = obj[key];
        }
    });
    return result;
};

/**
 * @param {Array} questions
 * @returns {Array}
 */
const orderQuestions = (questions) => {
    const list = Array.isArray(questions) ? [...questions] : [];
    const hasSortIndex = list.some((question) => {
        return question?.sort_index != null || question?.sortIndex != null;
    });

    if (!hasSortIndex) {
        return list;
    }

    return list.sort((a, b) => {
        const aIndex = a?.sort_index ?? a?.sortIndex ?? 0;
        const bIndex = b?.sort_index ?? b?.sortIndex ?? 0;
        return aIndex - bIndex;
    });
};

/**
 * Map a GDPR/TPDD-style nested option child (sub-question) into Designer shape.
 * @param {Object} child
 * @param {Function} t
 * @returns {Object}
 */
const mapGdprOptionChildToDesigner = (child, t) => {
    if (!isPlainObject(child)) {
        return child;
    }

    const {
        id,
        front_id: frontIdSnake,
        frontId,
        title,
        type,
        children,
        is_mandatory: isMandatorySnake,
        isMandatory,
        is_deletable: isDeletableSnake,
        isDeletable,
        ...rest
    } = child;

    return omitUndefined({
        ...rest,
        id: frontIdSnake || frontId || (id != null ? String(id) : generateFrontId()),
        dbID: id,
        baseTitle: title,
        title: t(title || ''),
        questionType: type,
        isMandatory: !!(isMandatory ?? isMandatorySnake),
        isDeletable: isDeletable != null || isDeletableSnake != null
            ? !!(isDeletable ?? isDeletableSnake)
            : true,
        options: Array.isArray(children)
            ? children.map((childOption) => mapGdprNestedOptionToDesigner(childOption, t))
            : []
    });
};

/**
 * Map nested option entries under a sub-question.
 * @param {Object} option
 * @param {Function} t
 * @returns {Object}
 */
const mapGdprNestedOptionToDesigner = (option, t) => {
    if (!isPlainObject(option)) {
        return option;
    }

    const {
        id,
        front_id: frontIdSnake,
        frontId,
        title,
        ...rest
    } = option;

    return omitUndefined({
        ...rest,
        id: frontIdSnake || frontId || (id != null ? String(id) : generateFrontId()),
        dbID: id,
        baseTitle: title,
        title: t(title || '')
    });
};

/**
 * Map a GDPR option into the shared Designer option shape.
 * Preserves GDPR-specific fields (reportingDeadlineHours, deleted, layout, …)
 * as custom properties on the Designer option object.
 *
 * Legacy radiobox follow-ups store the field type on `option.type` (e.g. `date`)
 * with an empty `children` array. The shared Designer expects a child
 * sub-question instead — same conversion as runtime `mapQuestions`.
 *
 * @param {Object} option
 * @param {Function} t
 * @param {string} [parentQuestionType]
 * @returns {Object}
 */
export const mapGdprOptionToDesigner = (
    option,
    t = identity,
    parentQuestionType
) => {
    if (!isPlainObject(option)) {
        return option;
    }

    const {
        id,
        front_id: frontIdSnake,
        frontId,
        title,
        type,
        children,
        extended_title: extendedTitle,
        ...rest
    } = option;

    void extendedTitle;

    const existingChildren = Array.isArray(children) ? children : [];
    let designerChildren;
    let optionType = type;

    if (
        existingChildren.length === 0 &&
        type &&
        supportsLegacyOptionTypeFollowUp(parentQuestionType) &&
        type !== parentQuestionType
    ) {
        designerChildren = [
            buildFollowUpChildFromOptionType(type, option, t, [])
        ];
        optionType = parentQuestionType;
    } else {
        designerChildren = existingChildren.map((child) => {
            return mapGdprOptionChildToDesigner(child, t);
        });
    }

    return omitUndefined({
        ...rest,
        id: frontIdSnake || frontId || generateFrontId(),
        dbID: id,
        baseTitle: title,
        title: t(title || ''),
        type: optionType,
        children: designerChildren
    });
};

/**
 * Map a flat GDPR question into a Designer question element.
 * @param {Object} question
 * @param {Function} t
 * @returns {Object}
 */
export const mapGdprQuestionToDesigner = (question, t = identity) => {
    if (!isPlainObject(question)) {
        return question;
    }

    const {
        id,
        front_id: frontIdSnake,
        frontId,
        title,
        type,
        is_mandatory: isMandatorySnake,
        isMandatory,
        is_deletable: isDeletableSnake,
        isDeletable,
        options,
        based_on: basedOnSnake,
        basedOn,
        section,
        sub_section: subSectionSnake,
        subSection,
        sort_index: sortIndexSnake,
        sortIndex,
        icon,
        system_data: systemDataSnake,
        systemData,
        ...rest
    } = question;

    // section / sub_section / sort_index / icon are consumed by page grouping
    // and are not duplicated onto the question element unless needed later.
    void section;
    void subSectionSnake;
    void subSection;
    void sortIndexSnake;
    void sortIndex;
    void icon;

    const resolvedSystemData = systemData || systemDataSnake || null;

    return omitUndefined({
        ...rest,
        id: frontIdSnake || frontId || generateFrontId(),
        dbID: id,
        elementType: 'question',
        baseTitle: title,
        title: t(title || ''),
        questionType: type,
        placeholder: title,
        isMandatory: !!(isMandatory ?? isMandatorySnake),
        isDeletable: isDeletable != null || isDeletableSnake != null
            ? !!(isDeletable ?? isDeletableSnake)
            : true,
        basedOn: basedOn || basedOnSnake,
        // Preserved for GDPR delete warnings; never sent on updateQuestionnaire.
        systemData: resolvedSystemData,
        options: Array.isArray(options)
            ? options.map((option) => {
                const mapped = mapGdprOptionToDesigner(option, t, type);

                // Category options are always checkboxes in the Designer UI.
                if (type === 'category') {
                    return {
                        ...mapped,
                        type: 'checkbox'
                    };
                }

                return mapped;
            })
            : []
    });
};

/**
 * Convert GDPR backend questions into the shared Designer `defaultModel` tree.
 *
 * Groups by backend-provided `section` → page, and `sub_section` → panel.
 * Does NOT invent section titles (no hardcoded "Page 1").
 * Questions without a section are grouped under an empty-string page title,
 * matching TPDD's `question.section || ''` behavior.
 *
 * @param {Array|Object} data - questions array, or `{ questions }`
 * @param {{ t?: Function }} [options]
 * @returns {Array} Designer pages tree
 */
export const gdprQuestionnaireToDesignerModel = (data, options = {}) => {
    const t = typeof options.t === 'function' ? options.t : identity;
    const questionsList = orderQuestions(
        Array.isArray(data) ? data : (data?.questions || [])
    );

    const pages = [];

    const findPage = (sectionName) => {
        return pages.find((page) => page.baseTitle === sectionName);
    };

    questionsList.forEach((question) => {
        // Ready for backend-provided section; empty string when absent (not invented).
        const sectionName = question?.section != null ? question.section : '';
        const subSectionName = question?.sub_section ?? question?.subSection;

        let page = findPage(sectionName);

        if (!page) {
            page = {
                elementType: 'page',
                baseTitle: sectionName,
                title: t(sectionName),
                image: question?.icon ?? null,
                elements: []
            };
            pages.push(page);
        } else if (page.image == null && question?.icon != null) {
            page.image = question.icon;
        }

        const designerQuestion = mapGdprQuestionToDesigner(question, t);

        if (subSectionName) {
            let panel = page.elements.find((element) => {
                return (
                    element.elementType === 'panel' &&
                    element.baseTitle === subSectionName
                );
            });

            if (!panel) {
                panel = {
                    elementType: 'panel',
                    baseTitle: subSectionName,
                    title: t(subSectionName),
                    elements: []
                };
                page.elements.push(panel);
            }

            panel.elements.push(designerQuestion);
        } else {
            page.elements.push(designerQuestion);
        }
    });

    // No empty "Page 1" fallback — return [] when there are no questions.
    return pages;
};

/**
 * Resolve Designer save payload / pages tree into a pages array.
 * @param {Array|Object} designerModel
 * @returns {Array}
 */
const normalizeDesignerPages = (designerModel) => {
    if (Array.isArray(designerModel)) {
        return designerModel;
    }

    if (isPlainObject(designerModel) && Array.isArray(designerModel.elements)) {
        return designerModel.elements;
    }

    if (isPlainObject(designerModel) && Array.isArray(designerModel.pages)) {
        return designerModel.pages;
    }

    return [];
};

/**
 * Designer-internal keys that must never reach the GDPR backend.
 *
 * `elements`, `parentElementID` and `state` are added by the shared Designer's
 * own element model (see serializeDesignerTree) and are rejected by the API.
 * `baseTitle` holds the originally loaded title, so it must not be used as the
 * saved value — edits only update `title`.
 */
const DESIGNER_INTERNAL_KEYS = [
    'id',
    'dbID',
    'frontId',
    'front_id',
    'baseTitle',
    'questionType',
    'elementType',
    'elements',
    'parentElementID',
    'state',
    'placeholder',
    'isUpdated',
    'newSubQuestionType'
];

/**
 * Copy backend-relevant fields, dropping Designer-internal metadata.
 *
 * @param {Object} obj
 * @param {Array<string>} [extraKeys] keys handled explicitly by the caller
 * @returns {Object}
 */
const omitDesignerInternals = (obj, extraKeys = []) => {
    const result = {};

    Object.keys(obj || {}).forEach((key) => {
        if (DESIGNER_INTERNAL_KEYS.includes(key) || extraKeys.includes(key)) {
            return;
        }

        if (obj[key] === undefined) {
            return;
        }

        result[key] = obj[key];
    });

    return result;
};

/**
 * Current (possibly edited) Designer title, falling back to the loaded title.
 * @param {Object} element
 * @returns {string|undefined}
 */
const resolveEditedTitle = (element) => {
    return element?.title != null ? element.title : element?.baseTitle;
};

/**
 * Map Designer option children (sub-questions) back to GDPR/TPDD nested shape.
 * @param {Object} child
 * @returns {Object}
 */
const mapDesignerOptionChildToGdpr = (child) => {
    if (!isPlainObject(child)) {
        return child;
    }

    const preserved = omitDesignerInternals(child, [
        'title',
        'type',
        'options',
        'children',
        'deleted',
        'isMandatory',
        'isDeletable'
    ]);

    return omitUndefined({
        ...preserved,
        id: child.dbID,
        title: resolveEditedTitle(child),
        type: child.questionType || child.type,
        children: Array.isArray(child.options) && child.options.length
            ? child.options.map((option) => mapDesignerNestedOptionToGdpr(option))
            : undefined
    });
};

/**
 * @param {Object} option
 * @returns {Object}
 */
const mapDesignerNestedOptionToGdpr = (option) => {
    if (!isPlainObject(option)) {
        return option;
    }

    const preserved = omitDesignerInternals(option, [
        'title',
        'deleted'
    ]);

    return omitUndefined({
        ...preserved,
        id: option.dbID,
        title: resolveEditedTitle(option)
    });
};

/**
 * Map a Designer option back to the GDPR save option shape.
 *
 * Uses the current (edited) option title, strips Designer-internal metadata and
 * `deleted` (the old GDPR Designer also stripped it), and preserves
 * reportingDeadlineHours plus any other GDPR-specific option properties.
 *
 * Legacy radiobox follow-ups that were expanded into a single Designer child
 * (no backend dbID) are flattened back to `option.type` so the GDPR save API
 * keeps receiving the old contract.
 *
 * @param {Object} option
 * @param {{ resolveCustomDeadline?: boolean, optionTypeFallback?: string }} [options]
 * @returns {Object}
 */
export const mapDesignerOptionToGdpr = (option, options = {}) => {
    if (!isPlainObject(option)) {
        return option;
    }

    const resolveCustomDeadline = options.resolveCustomDeadline !== false;
    const parentType = options.optionTypeFallback;

    const preserved = omitDesignerInternals(option, [
        'title',
        'type',
        'children',
        'deleted',
        'reportingDeadlineHours',
        'customReportingDeadlineHours',
        'extended_title',
        'extendedTitle'
    ]);

    let deadline = option.reportingDeadlineHours;

    if (resolveCustomDeadline && deadline === 'custom') {
        deadline = option.customReportingDeadlineHours;
    }

    const designerChildren = Array.isArray(option.children)
        ? option.children
        : [];

    let type = option.type || parentType;
    let mappedChildren;

    const firstChild = designerChildren[0];
    const firstChildType = firstChild?.questionType || firstChild?.type;
    const isLegacyFollowUpChild =
        designerChildren.length === 1 &&
        supportsLegacyOptionTypeFollowUp(parentType) &&
        firstChild?.dbID == null &&
        firstChildType &&
        firstChildType !== parentType;

    if (isLegacyFollowUpChild) {
        // Old GDPR contract: follow-up field type lives on the option.
        type = firstChildType;
        mappedChildren = undefined;
    } else if (designerChildren.length) {
        mappedChildren = designerChildren.map((child) => {
            return mapDesignerOptionChildToGdpr(child);
        });
    }

    return omitUndefined({
        ...preserved,
        id: option.dbID,
        title: resolveEditedTitle(option),
        type,
        reportingDeadlineHours:
            deadline === '' || deadline == null ? undefined : deadline,
        children: mappedChildren
    });
};

/**
 * Map a Designer question element to a GDPR save question.
 *
 * @param {Object} question
 * @param {Object} page
 * @param {Object|undefined} panel
 * @param {number} sortIndex
 * @param {{
 *   resolveCustomDeadline?: boolean,
 *   includeSections?: boolean
 * }} [options]
 * @returns {Object}
 */
export const mapDesignerQuestionToGdpr = (
    question,
    page,
    panel,
    sortIndex,
    options = {}
) => {
    if (!isPlainObject(question)) {
        return question;
    }

    const includeSections = options.includeSections !== false;

    const preserved = omitDesignerInternals(question, [
        'title',
        'type',
        'options',
        'basedOn',
        'section',
        'subSection',
        'sub_section',
        'sortIndex',
        'sort_index',
        'icon',
        'isMandatory',
        'isDeletable',
        // Backend update rejects unknown fields; system_data is read-only.
        'systemData',
        'system_data'
    ]);

    const questionType = question.questionType || question.type;

    const mappedOptions =
        Array.isArray(question.options) && question.options.length
            ? question.options.map((option) => {
                return mapDesignerOptionToGdpr(option, {
                    ...options,
                    // Category options are always checkboxes.
                    optionTypeFallback:
                        questionType === 'category' ? 'checkbox' : questionType
                });
            })
            : undefined;

    const result = {
        ...preserved,
        id: question.dbID,
        title: resolveEditedTitle(question),
        type: questionType,
        sortIndex,
        isMandatory: !!question.isMandatory,
        isDeletable:
            question.isDeletable !== undefined ? !!question.isDeletable : true,
        options: mappedOptions
    };

    if (includeSections) {
        const section = page?.baseTitle || page?.title;
        const subSection = panel ? (panel.baseTitle || panel.title) : undefined;

        // Never invent values: empty page titles (GDPR data without `section`)
        // produce no section/subSection at all.
        result.section = section ? section : undefined;
        result.subSection = subSection ? subSection : undefined;
        result.icon = page?.image || undefined;
    }

    if (question.basedOn) {
        result.basedOn = question.basedOn;
    }

    return omitUndefined(result);
};

/**
 * Convert shared Designer model / save callback payload into the existing
 * GDPR save API structure.
 *
 * @param {Array|Object} designerModel - pages tree, or `{ elements }` / `{ title, elements }`
 * @param {{
 *   questionnaireId?: number|string,
 *   resolveCustomDeadline?: boolean,
 *   includeSections?: boolean
 * }} [options]
 * @returns {{ questions: Array, questionnaire?: number|string }}
 */
export const designerModelToGdprQuestionnaire = (designerModel, options = {}) => {
    const pages = normalizeDesignerPages(designerModel);
    const questions = [];
    let sortIndex = 0;

    pages.forEach((page) => {
        (page?.elements || []).forEach((element) => {
            if (element?.elementType === 'question') {
                questions.push(
                    mapDesignerQuestionToGdpr(
                        element,
                        page,
                        undefined,
                        sortIndex++,
                        options
                    )
                );
                return;
            }

            if (element?.elementType === 'panel') {
                (element.elements || []).forEach((panelQuestion) => {
                    questions.push(
                        mapDesignerQuestionToGdpr(
                            panelQuestion,
                            page,
                            element,
                            sortIndex++,
                            options
                        )
                    );
                });
            }
        });
    });

    const payload = {
        questions
    };

    if (options.questionnaireId != null) {
        payload.questionnaire = options.questionnaireId;
    }

    return payload;
};

export default {
    gdprQuestionnaireToDesignerModel,
    designerModelToGdprQuestionnaire,
    mapGdprQuestionToDesigner,
    mapGdprOptionToDesigner,
    mapDesignerQuestionToGdpr,
    mapDesignerOptionToGdpr
};
